/**
 * pricingRuleDetailModel — pure, presentation-only helpers shared by the custom
 * Pricing Rule detail slots (header badge, promotion summary, sidebar).
 *
 * All helpers are language-aware (Thai-first UI): pass `lang` from useLanguage().
 * Everything reads straight off the Pricing Rule `formData` — no extra fetches.
 * Field names match what the create wizard writes (see pricingRuleWizardModel.ts).
 */

import { formatMoney, formatReadableDate } from "@/features/customers/customer-detail/customerDetailPrimitives";

type Doc = Record<string, any>;
export type Lang = "en" | "th";

const num = (v: unknown): number => (typeof v === "number" ? v : parseFloat(String(v ?? "")) || 0);
const truthy = (v: unknown): boolean => v === 1 || v === "1" || v === true;
const pick = (lang: Lang, th: string, en: string) => (lang === "th" ? th : en);

/** Currency the rule is denominated in (falls back to THB). */
export function ruleCurrency(doc: Doc): string {
  return doc.currency || "THB";
}

/** Is this rule triggered by a coupon code, or applied automatically? */
export function isCouponRule(doc: Doc): boolean {
  return truthy(doc.coupon_code_based);
}

/** Is this a free-item (product) reward rather than a price discount? */
export function isProductReward(doc: Doc): boolean {
  return doc.price_or_product_discount === "Product";
}

/**
 * Does this rule grant a core reward (a price discount or a free item), as
 * opposed to only perks? Used to decide whether perks are shown separately or
 * are already the headline reward (avoids listing "free shipping" twice).
 */
export function hasCoreReward(doc: Doc): boolean {
  if (isProductReward(doc)) return (num(doc.free_qty) || 0) > 0 || truthy(doc.same_item);
  return num(doc.discount_percentage) > 0 || num(doc.discount_amount) > 0 || num(doc.rate) > 0;
}

/** Active perks as short chips (free shipping / free personalization). */
export function perkChips(doc: Doc, lang: Lang = "en"): string[] {
  const out: string[] = [];
  if (doc.custom_shipping_discount_type) {
    out.push(
      doc.custom_shipping_discount_type === "Free Shipping"
        ? pick(lang, "ส่งฟรี", "Free shipping")
        : pick(lang, "ส่วนลดค่าส่ง", "Shipping discount"),
    );
  }
  if (truthy(doc.custom_is_embroidery_coupon)) out.push(pick(lang, "ปรับแต่งฟรี", "Free personalization"));
  return out;
}

/** The reward as a compact human phrase, e.g. "ลด 10%" / "10% off". */
export function rewardSummary(doc: Doc, lang: Lang = "en"): string {
  if (isProductReward(doc)) {
    const qty = num(doc.free_qty) || 1;
    if (truthy(doc.same_item)) return pick(lang, `แถมสินค้าเดียวกัน ×${qty}`, `Free — same item ×${qty}`);
    return doc.free_item
      ? pick(lang, `แถม: ${doc.free_item} ×${qty}`, `Free: ${doc.free_item} ×${qty}`)
      : pick(lang, `แถมสินค้า ×${qty}`, `Free item ×${qty}`);
  }
  const pct = num(doc.discount_percentage);
  const amt = num(doc.discount_amount);
  const rate = num(doc.rate);
  if (doc.rate_or_discount === "Discount Percentage" && pct > 0) return pick(lang, `ลด ${pct}%`, `${pct}% off`);
  if (doc.rate_or_discount === "Discount Amount" && amt > 0)
    return pick(lang, `ลด ${formatMoney(amt, ruleCurrency(doc))}`, `${formatMoney(amt, ruleCurrency(doc))} off`);
  if (doc.rate_or_discount === "Rate" && rate > 0)
    return pick(lang, `ตั้งราคา ${formatMoney(rate, ruleCurrency(doc))}`, `Set price ${formatMoney(rate, ruleCurrency(doc))}`);
  // No price discount configured — the benefit is a perk (free shipping / etc.).
  const perks = perkChips(doc, lang);
  return perks.length > 0 ? perks.join(" + ") : "—";
}

/**
 * Business fields that define WHAT a promotion does — copied verbatim when the
 * owner duplicates a rule. Deliberately excludes identity/system columns (name,
 * owner, creation, modified, docstatus, idx, coupon_code linkage) so the clone
 * is a brand-new draft, not a shadow of the original.
 */
const DUPLICABLE_SCALAR_FIELDS = [
  // reward — price discount
  "price_or_product_discount", "rate_or_discount", "rate", "discount_percentage",
  "discount_amount", "apply_discount_on", "max_discount_amount", "for_price_list",
  "margin_type", "margin_rate_or_amount",
  // reward — free item
  "same_item", "free_item", "free_qty", "free_item_rate", "free_item_uom",
  "round_free_qty", "dont_enforce_free_item_qty", "is_recursive", "recurse_for",
  "apply_recursion_over",
  // perks
  "custom_shipping_discount_type", "custom_shipping_discount_value",
  "custom_is_embroidery_coupon",
  // scope
  "apply_on", "applicable_for", "selling", "buying", "customer", "customer_group",
  "territory", "sales_partner", "campaign", "supplier", "supplier_group",
  "item_code", "item_group", "brand", "warehouse", "mixed_conditions",
  "is_cumulative", "apply_rule_on_other", "other_item_code", "other_item_group",
  "other_brand",
  // conditions & validity
  "min_qty", "max_qty", "min_amt", "max_amt", "valid_from", "valid_upto",
  "custom_campaign_start", "custom_campaign_end", "currency", "has_priority",
  "priority",
  // stacking / advanced
  "apply_multiple_pricing_rules", "apply_discount_on_rate", "threshold_percentage",
  "validate_applied_rule", "rule_description", "apply_on_prev_doc", "condition",
  // coupon flag + storefront visibility
  "coupon_code_based", "storefront_enabled", "storefront_show_on_detail",
  "custom_storefront_scope",
] as const;

/**
 * Build the create() payload for "Duplicate this promo": a faithful copy of the
 * current rule's reward / scope / condition config, always born as a DISABLED
 * draft (`disable = 1`) so it can never touch live checkout until the owner
 * reviews it, and titled "<title> (copy)". Feeds the same pricingRules.create
 * path the wizard already uses — no new endpoint.
 *
 * Note on coupon rules: the redeemable code lives on a separate Coupon Code doc,
 * not on the Pricing Rule, so the clone carries the coupon *behaviour* but no
 * code — the owner assigns a fresh code on review. While disabled + code-less it
 * is inert, which is exactly the safe draft state we want.
 */
export function buildDuplicatePayload(doc: Doc): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of DUPLICABLE_SCALAR_FIELDS) {
    const v = doc[f];
    if (v !== undefined && v !== null && v !== "") out[f] = v;
  }

  // Child tables — strip Frappe row identity (name/parent/idx/docstatus).
  if (Array.isArray(doc.items) && doc.items.length)
    out.items = doc.items.map((r: any) => ({ item_code: r.item_code, ...(r.uom ? { uom: r.uom } : {}) }));
  if (Array.isArray(doc.item_groups) && doc.item_groups.length)
    out.item_groups = doc.item_groups.map((r: any) => ({ item_group: r.item_group }));
  if (Array.isArray(doc.brands) && doc.brands.length)
    out.brands = doc.brands.map((r: any) => ({ brand: r.brand }));

  const baseTitle = doc.title || doc.name || "Promotion";
  out.title = `${baseTitle} (copy)`;
  out.disable = 1; // born OFF — a reviewable draft, never live on creation

  return out;
}

/** Short reward value + unit for the header badge, e.g. {value:"10%", unit:"OFF"}. */
export function rewardBadge(doc: Doc, lang: Lang = "en"): { value: string; unit: string } {
  if (isProductReward(doc)) return lang === "th" ? { value: "แถม", unit: "ฟรี" } : { value: "FREE", unit: "ITEM" };
  const pct = num(doc.discount_percentage);
  const amt = num(doc.discount_amount);
  const rate = num(doc.rate);
  if (doc.rate_or_discount === "Discount Percentage" && pct > 0)
    return { value: `${pct}%`, unit: pick(lang, "ลด", "OFF") };
  if (doc.rate_or_discount === "Discount Amount" && amt > 0)
    return { value: formatMoney(amt, ruleCurrency(doc)), unit: pick(lang, "ลด", "OFF") };
  if (doc.rate_or_discount === "Rate" && rate > 0)
    return lang === "th" ? { value: "ราคา", unit: "พิเศษ" } : { value: "SET", unit: "PRICE" };
  // Perk-only rule (free shipping / personalization) — no numeric discount.
  if (perkChips(doc, lang).length > 0) return lang === "th" ? { value: "ฟรี", unit: "สิทธิ" } : { value: "FREE", unit: "PERK" };
  return { value: "—", unit: "" };
}

/** What the rule applies to, e.g. "ทั้งตะกร้า" / "Whole cart". */
export function appliesToSummary(doc: Doc, lang: Lang = "en"): string {
  switch (doc.apply_on) {
    case "Item Code": {
      const items = (doc.items ?? []) as { item_code?: string }[];
      if (items.length === 1) return items[0].item_code || pick(lang, "1 รายการ", "1 product");
      if (items.length > 1) return pick(lang, `${items.length} รายการ`, `${items.length} products`);
      return doc.item_code || pick(lang, "สินค้าที่เลือก", "Specific product");
    }
    case "Item Group": {
      const groups = (doc.item_groups ?? []) as { item_group?: string }[];
      if (groups.length === 1) return groups[0].item_group || pick(lang, "1 หมวดหมู่", "1 category");
      if (groups.length > 1) return pick(lang, `${groups.length} หมวดหมู่`, `${groups.length} categories`);
      return doc.item_group || pick(lang, "หมวดหมู่", "Category");
    }
    case "Brand": {
      const brands = (doc.brands ?? []) as { brand?: string }[];
      if (brands.length === 1) return brands[0].brand || pick(lang, "1 แบรนด์", "1 brand");
      if (brands.length > 1) return pick(lang, `${brands.length} แบรนด์`, `${brands.length} brands`);
      return doc.brand || pick(lang, "แบรนด์", "Brand");
    }
    case "Transaction":
    default:
      return pick(lang, "ทั้งตะกร้า", "Whole cart");
  }
}

export type StatusTone = "active" | "scheduled" | "expired" | "disabled";

/** Lifecycle status derived from disable flag + campaign/validity window. */
export function promotionStatus(
  doc: Doc,
  now: Date = new Date(),
  lang: Lang = "en",
): { label: string; tone: StatusTone } {
  if (truthy(doc.disable)) return { label: pick(lang, "ปิดใช้งาน", "Disabled"), tone: "disabled" };

  // Prefer the hour-precise campaign window; fall back to the date-level fields.
  const start = doc.custom_campaign_start || doc.valid_from;
  const end = doc.custom_campaign_end || doc.valid_upto;
  const parse = (v?: string) => {
    if (!v) return null;
    const d = new Date(String(v).includes("T") ? v : String(v).replace(" ", "T"));
    return isNaN(d.getTime()) ? null : d;
  };
  const s = parse(start);
  const e = parse(end);
  if (s && now < s) return { label: pick(lang, "ตั้งเวลาไว้", "Scheduled"), tone: "scheduled" };
  if (e && now > e) return { label: pick(lang, "หมดอายุ", "Expired"), tone: "expired" };
  return { label: pick(lang, "กำลังใช้งาน", "Active"), tone: "active" };
}

/** Campaign window as a readable range, or "ไม่มีวันสิ้นสุด" / "Always on". */
export function windowSummary(doc: Doc, lang: Lang = "en"): string {
  const from = doc.valid_from ? formatReadableDate(doc.valid_from) : null;
  const upto = doc.valid_upto ? formatReadableDate(doc.valid_upto) : null;
  if (!from && !upto) return pick(lang, "ตลอดเวลา", "Always on");
  if (from && !upto) return pick(lang, `ตั้งแต่ ${from}`, `From ${from}`);
  if (!from && upto) return pick(lang, `ถึง ${upto}`, `Until ${upto}`);
  return `${from} → ${upto}`;
}

/**
 * A short, time-sensitive nudge for the header subtitle, e.g.
 * "เริ่มในอีก 2 วัน" (scheduled) / "เหลืออีก 5 วัน" (active) / "หมดอายุแล้ว".
 * Returns null when there's nothing time-bound to say.
 */
export function promotionCountdown(doc: Doc, now: Date = new Date(), lang: Lang = "en"): string | null {
  if (truthy(doc.disable)) return null;
  const parse = (v?: string) => {
    if (!v) return null;
    const d = new Date(String(v).includes("T") ? v : String(v).replace(" ", "T"));
    return isNaN(d.getTime()) ? null : d;
  };
  const start = parse(doc.custom_campaign_start || doc.valid_from);
  const end = parse(doc.custom_campaign_end || doc.valid_upto);
  const days = (target: Date) => Math.ceil((target.getTime() - now.getTime()) / 86_400_000);

  if (start && now < start) {
    const d = days(start);
    if (d <= 0) return pick(lang, "เริ่มวันนี้", "Starts today");
    if (d === 1) return pick(lang, "เริ่มพรุ่งนี้", "Starts tomorrow");
    return pick(lang, `เริ่มในอีก ${d} วัน`, `Starts in ${d} days`);
  }
  if (end) {
    if (now > end) return pick(lang, "หมดอายุแล้ว", "Expired");
    const d = days(end);
    if (d <= 0) return pick(lang, "สิ้นสุดวันนี้", "Ends today");
    if (d === 1) return pick(lang, "เหลืออีก 1 วัน", "1 day left");
    return pick(lang, `เหลืออีก ${d} วัน`, `${d} days left`);
  }
  return null;
}

/** Storefront scope → list of storefront keys (empty = all storefronts). */
export function storefrontScopeList(doc: Doc): string[] {
  const raw = String(doc.custom_storefront_scope ?? "").trim();
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}
